import React, { useState } from "react";
import { Col, Container, Form, Row, Card, Spinner, Alert } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBell,
    faArrowLeft,
    faCheckCircle,
    faExclamationCircle,
    faInfoCircle,
    faShieldAlt,
    faExternalLinkAlt,
    faServer,
    faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest } from "../../../../Helper/ApiClient";
import CodeBlock from "../../../../Abstracts/CodeBlock/CodeBlock";

export default function Stock() {
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    const getUserApiSettings = getRequest('/api/api_settings');
    const updateUserApiSettingMutation = postRequest('/api/api_settings');

    const hasEndpoint = getUserApiSettings.isSuccess && getUserApiSettings.data?.stock_endpoint_url?.length > 0;
    const endpointUrl = getUserApiSettings.data?.stock_endpoint_url || '';
    const authToken = getUserApiSettings.data?.stock_endpoint_auth_header || '';

    const inputOnChangeHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        setSaveStatus('saving');
        let formData = new FormData();
        formData.append("name", event.currentTarget.name);
        formData.append("value", event.currentTarget.value);
        updateUserApiSettingMutation.mutate(formData, {
            onSuccess: () => {
                getUserApiSettings.refetch();
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            }
        });
    };

    return (
        <Container className="py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <NavLink
                        to="/service-center/api"
                        className="text-decoration-none text-muted d-inline-flex align-items-center mb-3 text-14"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                        Back to API Overview
                    </NavLink>
                    <h1 className="mb-2">
                        <FontAwesomeIcon icon={faBell} className="me-3 text-primary" />
                        Live Stock Updates
                    </h1>
                    <p className="text-muted mb-0">
                        Receive real-time inventory notifications when stock levels change.
                    </p>
                </Col>
            </Row>

            {/* Status Card */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card
                        className="border-0"
                        style={{
                            backgroundColor: hasEndpoint ? '#e8f5e9' : '#fff3e0',
                            borderLeft: `4px solid ${hasEndpoint ? '#4caf50' : '#ff9800'}`,
                            borderRadius: 8
                        }}
                    >
                        <Card.Body className="d-flex align-items-center gap-3 py-3">
                            <div
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: "white",
                                    color: hasEndpoint ? '#4caf50' : '#ff9800'
                                }}
                            >
                                {getUserApiSettings.isPending ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    <FontAwesomeIcon icon={hasEndpoint ? faCheckCircle : faExclamationCircle} />
                                )}
                            </div>
                            <div>
                                <div className="fw-semibold">
                                    {getUserApiSettings.isPending
                                        ? 'Loading...'
                                        : hasEndpoint
                                            ? 'Stock Endpoint Configured'
                                            : 'No Endpoint Configured'
                                    }
                                </div>
                                <div style={{ color: '#666' }} className="text-12">
                                    {hasEndpoint
                                        ? 'You will receive stock updates at your endpoint'
                                        : 'Configure an endpoint to start receiving stock updates'
                                    }
                                </div>
                                {hasEndpoint && (
                                    <code
                                        className="d-block text-truncate mt-1 text-10"
                                        style={{ maxWidth: '100%' }}
                                        title={endpointUrl}
                                    >
                                        {endpointUrl}
                                    </code>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* How it Works */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: 12 }}>
                        <Card.Body className="p-4">
                            <h2 className="mb-3">
                                <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" />
                                How Stock Push Works
                            </h2>
                            <div className="d-flex align-items-center gap-3 mb-4">
                                <div className="text-center">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                                        style={{ width: 50, height: 50, backgroundColor: '#e3f2fd', color: '#1976d2' }}
                                    >
                                        <FontAwesomeIcon icon={faServer} />
                                    </div>
                                    <div className="text-12">NaskorSports</div>
                                </div>
                                <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
                                <div className="text-center flex-grow-1">
                                    <div
                                        className="rounded px-3 py-2 mx-auto mb-2 text-12"
                                        style={{ backgroundColor: '#e8f5e9', color: '#388e3c',}}
                                    >
                                        POST /stock
                                    </div>
                                    <div style={{ color: '#666' }} className="text-12">Stock data pushed</div>
                                </div>
                                <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
                                <div className="text-center">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2"
                                        style={{ width: 50, height: 50, backgroundColor: '#fff3e0', color: '#f57c00' }}
                                    >
                                        <FontAwesomeIcon icon={faBell} />
                                    </div>
                                    <div className="text-12">Your System</div>
                                </div>
                            </div>
                            <p className="mb-2 text-14">
                                When stock levels change for products you have mapped with SKU references,
                                we automatically push updates to your endpoint.
                            </p>
                            <ul className="mb-0 text-14">
                                <li>We append <code>/stock</code> to your base URL</li>
                                <li>Updates include your custom SKU references</li>
                                <li>Only mapped products trigger notifications</li>
                            </ul>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Configuration Form */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h2 className="mb-0">Endpoint Configuration</h2>
                                {saveStatus === 'saving' && (
                                    <span className="text-muted text-12">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </span>
                                )}
                                {saveStatus === 'saved' && (
                                    <span className="text-success text-12">
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                                        Saved
                                    </span>
                                )}
                            </div>

                            <Form.Group className="mb-4">
                                <Form.Label className="fw-semibold">
                                    <FontAwesomeIcon icon={faServer} className="me-2 text-muted" />
                                    Endpoint URL
                                </Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://your-api.com/webhooks"
                                    onBlur={inputOnChangeHandler}
                                    name="api_url"
                                    defaultValue={endpointUrl}
                                    style={{ fontFamily: 'monospace' }}
                                />
                                <Form.Text className="text-muted">
                                    We will send POST requests to <code>{endpointUrl || 'your-url'}/stock</code>
                                </Form.Text>
                            </Form.Group>

                            <Form.Group>
                                <Form.Label className="fw-semibold">
                                    <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-muted" />
                                    Authorization Token <span className="fw-normal text-muted">(optional)</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Bearer your-secret-token"
                                    onBlur={inputOnChangeHandler}
                                    name="authorization_token"
                                    defaultValue={authToken}
                                    style={{ fontFamily: 'monospace' }}
                                />
                                <Form.Text className="text-muted">
                                    This token will be sent in the <code>X-Authorization</code> header
                                </Form.Text>
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Example Payload */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: 12 }}>
                        <Card.Body className="p-4">
                            <h2 className="mb-3">Example Stock Update Request</h2>
                            <CodeBlock language="http" title={`POST ${endpointUrl || 'https://your-api.com'}/stock`}>
                                {`Content-Type: application/x-www-form-urlencoded
X-Authorization: your-token

customer_reference=YOUR-SKU-123&stock=100`}
                            </CodeBlock>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Documentation Link */}
            <Row>
                <Col md={8}>
                    <Alert variant="info" className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-3" />
                        <div>
                            <strong>API Specification:</strong>{' '}
                            View the full endpoint specification on{' '}
                            <a
                                href="https://editor.swagger.io/?url=https://rest.naskorsports.com/customer-end-api/v1/documentation"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Swagger UI <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                            </a>
                        </div>
                    </Alert>
                </Col>
            </Row>
        </Container>
    );
}
